import { useCallback, useRef } from 'react';
import { webchatApi } from '../services/api';
import { store } from '../state/store';
import { syncI18nLocale } from '../utils/locale';
import type { McpChatWidgetProps } from '../interfaces/mcpChatWidgetProps';
import {
  buildAssistantMessage,
  buildConfigFromProps,
  getGreetingContent,
  mergeRemoteConfig,
} from './helpers';
import type { McpWidgetRuntimeRefs } from './types';

interface UseWidgetConfigRuntimeArgs {
  props: McpChatWidgetProps;
  refs: McpWidgetRuntimeRefs;
  closeMcpSession(): Promise<void>;
  fetchAnonymousToken(): Promise<void>;
}

export function useWidgetConfigRuntime({
  props,
  refs,
  closeMcpSession,
  fetchAnonymousToken,
}: UseWidgetConfigRuntimeArgs) {
  const titleOverride = props.title ?? null;
  const greetingOverride = props.greeting ?? null;
  const otpGreetingOverride = props.otpGreeting ?? null;

  // Always-current ref so async callbacks (health check) read the latest value
  // even when the useCallback closure was created before the attribute was set.
  const portalModeRef = useRef(props.portalMode);
  portalModeRef.current = props.portalMode;

  const syncAttributesToConfig = useCallback((nextProps: McpChatWidgetProps): void => {
    const { partial, locale } = buildConfigFromProps(nextProps, store.getState().config.authProvider);

    if (locale) {
      syncI18nLocale(locale);
    }

    const prevAuth = store.getState().config.auth;
    store.setConfig(partial);
    const newAuth = store.getState().config.auth;

    if (prevAuth !== newAuth && prevAuth !== undefined) {
      store.setState({ messages: [], tools: [], toolCalls: [] });
      void closeMcpSession();
    }

    if (newAuth === 'none' && store.getState().auth.status !== 'authenticated') {
      store.setAuth({ status: 'authenticated', email: null, token: null });
      if (!nextProps.preview) {
        refs.anonymousTokenPromiseRef.current = fetchAnonymousToken();
      }
    }

    if (prevAuth === 'none' && newAuth === 'required') {
      store.setAuth({ status: 'unauthenticated', email: null, token: null });
    }
  }, [closeMcpSession, fetchAnonymousToken, refs.anonymousTokenPromiseRef]);

  const seedGreeting = useCallback((): void => {
    if (store.getState().messages.length > 0) return;
    const { config, auth } = store.getState();
    store.addMessage(buildAssistantMessage(getGreetingContent(config, auth), true));
  }, []);

  const updateGreetingForLanguage = useCallback((): void => {
    const { messages, config, auth } = store.getState();
    const firstMessage = messages[0];
    if (!firstMessage?.isGreeting) return;
    syncI18nLocale(config.language);
    store.updateMessage(firstMessage.id, { content: getGreetingContent(config, auth) });
  }, []);

  const performHealthCheck = useCallback(async (): Promise<void> => {
    try {
      const health = await webchatApi.health();
      const currentAuth = store.getState().auth;
      const currentConfig = store.getState().config;
      let nextAuth = currentAuth;
      const serverAuthProvider = health.authProvider ?? (health.otpEnabled ? 'otp' : 'none');
      const requiresIdentifiedAuth = serverAuthProvider !== 'none';
      const shouldStayAnonymous = currentConfig.auth === 'none';

      if (shouldStayAnonymous) {
        nextAuth = {
          status: 'authenticated',
          email: null,
          token: currentAuth.token,
        };
        store.setAuth(nextAuth);
      } else if (health.authenticated && (portalModeRef.current || !(health.anonymous && requiresIdentifiedAuth))) {
        // In portal mode the user is already authenticated by the admin SPA - accept their
        // existing session directly without requiring SSO re-authentication.
        nextAuth = {
          status: 'authenticated',
          email: health.anonymous ? null : (health.user?.email ?? currentAuth.email),
          token: currentAuth.token,
        };
        store.setAuth(nextAuth);
      } else if (
        currentAuth.status === 'authenticated'
        && (currentAuth.token || store.getState().config.auth === 'required')
      ) {
        nextAuth = { status: 'unauthenticated', email: null, token: null };
        store.setAuth(nextAuth);
      }

      if (typeof health.uploadEnabled === 'boolean') {
        store.setConfig({ uploadEnabled: health.uploadEnabled });
      }

      const authProviderChanged = currentConfig.authProvider !== serverAuthProvider;
      store.setConfig({ authProvider: serverAuthProvider });

      if (serverAuthProvider !== 'none' && store.getState().config.auth === 'none') {
        store.setConfig({ auth: 'required' });
        store.setAuth({ status: 'unauthenticated', email: null, token: null });
        store.setState({ messages: [] });
        void closeMcpSession();
        nextAuth = store.getState().auth;
      } else if (serverAuthProvider === 'none' && store.getState().auth.status !== 'authenticated') {
        // Server requires no auth but widget isn't authenticated yet (e.g. portal opened without
        // an explicit auth='none' prop, or admin cookie was invalid/expired).
        store.setConfig({ auth: 'none' });
        store.setAuth({ status: 'authenticated', email: null, token: null });
        if (!props.preview) {
          refs.anonymousTokenPromiseRef.current = fetchAnonymousToken();
        }
        nextAuth = store.getState().auth;
      }

      if (currentAuth.status !== nextAuth.status || authProviderChanged) {
        updateGreetingForLanguage();
      }
    } catch {
      console.warn('[mcp-chat-widget] Health check failed');
    }

    try {
      const config = await webchatApi.getConfig();
      const { partial, shouldUpdateGreeting, isInactive } = mergeRemoteConfig(config, {
        title: titleOverride,
        greeting: greetingOverride,
        otpGreeting: otpGreetingOverride,
      });

      if (isInactive) {
        store.setState({ widgetInactive: true });
      }

      if (Object.keys(partial).length > 0) {
        if (partial.language) {
          syncI18nLocale(partial.language);
        }
        store.setConfig(partial);
        if (shouldUpdateGreeting || !!partial.language) {
          updateGreetingForLanguage();
        }
      }
    } catch {
      // Config endpoint may not exist on older servers.
    }
  }, [
    closeMcpSession,
    greetingOverride,
    otpGreetingOverride,
    titleOverride,
    updateGreetingForLanguage,
  ]);

  const checkHealth = useCallback(async (): Promise<void> => {
    const healthPromise = performHealthCheck();
    refs.healthCheckPromiseRef.current = healthPromise;
    await healthPromise;
  }, [performHealthCheck, refs.healthCheckPromiseRef]);

  return {
    syncAttributesToConfig,
    seedGreeting,
    updateGreetingForLanguage,
    performHealthCheck,
    checkHealth,
  };
}
